'use client';

import { ISalePageCommentResponse } from '@/api/types';
import { getRouterPath } from '@/components';
import { useUserQuery } from '@/entities/auth';
import { useSalePageQuery } from '@/entities/salePage';
import { useLoginInducingModal } from '@/hooks/useLoginInducingModal';
import { qaAttr, qaId } from '@/lib/qa';
import { Element, Row } from '@vuddy/components';
import { isNonNil, trackEvent } from '@vuddy/utils';
import { isNil } from 'lodash-es';
import { useRouter } from 'next/navigation';
import { SalePageCommentInput } from '../commentInput';
import { SalePageCommentsList } from './CommentList';
import * as styles from './comments.css';

interface SalePageCommentsProps {
  salePageId?: number;
  commentsCount: number;
  comments: ISalePageCommentResponse[];
  interactive?: boolean;
}

export const SalePageComments = ({
  salePageId,
  commentsCount,
  comments,
  interactive,
}: SalePageCommentsProps) => {
  const salePageQuery = useSalePageQuery(salePageId);
  const userQuery = useUserQuery();
  const loginConfirm = useLoginInducingModal();
  const router = useRouter();
  const commentsPagePath = isNonNil(salePageId)
    ? getRouterPath().comments(salePageId)
    : '';

  const titleLabel = salePageQuery.data?.type === 'CLASS' ? '후기' : '댓글';

  const getAnalyticsProperties = () => {
    return {
      channel_id: salePageQuery.data?.channelId,
      sale_type: salePageQuery.data?.type,
    };
  };

  const handleCommentsClick = () => {
    // 로그인 체크
    if (!userQuery.isSuccess || isNil(userQuery.data)) {
      loginConfirm();
      return;
    }

    // 로그인된 경우 댓글 상세 페이지로 이동
    trackEvent('shop', 'click_sale_comment', getAnalyticsProperties());
    router.push(commentsPagePath);
  };

  const saleType = salePageQuery.data?.type;

  return (
    <div
      {...qaAttr(qaId('sale', 'comments'))}
      id="comments"
      className={styles.wrapper({
        interactive,
        saleType: saleType === 'CLASS' ? 'CLASS' : 'default',
      })}>
      <Row alignItems="center" justifyContent="space-between">
        <Element typography="titleBase">
          {titleLabel} {commentsCount}
        </Element>
      </Row>
      <Element
        as="div"
        onClick={handleCommentsClick}
        className={styles.clickableArea}>
        <SalePageCommentInput
          interactive={false}
          commentLabel={titleLabel}
          qaId={qaId('sale', 'comment.input')}
        />
        <SalePageCommentsList
          salePageId={salePageId}
          comments={comments}
          interactive={false}
          commentLabel={titleLabel}
        />
      </Element>
    </div>
  );
};
